import { useState, useEffect, useCallback } from 'react'
import { Badge, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { defineMessages, useIntl } from 'react-intl'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { URL } from '../../../gql/client'
import { parseId } from '../../../helpers'

/** Marca comercial: no se traduce. */
const ezlynx = 'EZLynx'

const ezM = defineMessages({
  synced: {id: 'leads.ezlynx.synced', defaultMessage: 'EZLynx Synced'},
  applicantId: {id: 'leads.ezlynx.applicantId', defaultMessage: 'Applicant ID:'},
  notSyncedTooltip: {
    id: 'leads.ezlynx.notSyncedTooltip',
    defaultMessage: 'Not synced with EZLynx. Click "Sync" to push this lead.',
  },
  syncing: {id: 'leads.ezlynx.syncing', defaultMessage: 'Syncing...'},
  syncAction: {id: 'leads.ezlynx.syncAction', defaultMessage: 'Sync to EZLynx'},
  resyncTooltip: {id: 'leads.ezlynx.resyncTooltip', defaultMessage: 'Re-sync lead data'},
  errorSyncFailed: {id: 'leads.ezlynx.error.syncFailed', defaultMessage: 'Sync failed'},
  errorSyncing: {id: 'leads.ezlynx.error.syncing', defaultMessage: 'Error syncing to EZLynx'},
})

interface EZLynxStatus {
  synced: boolean
  external_id: string | null
  synced_at: string | null
}

interface Props {
  leadId: string  // Relay ID o numeric string
  showButton?: boolean
}

export const EZLynxSyncBadge: React.FC<Props> = ({ leadId, showButton = true }) => {
  const intl = useIntl()
  const { auth } = useAuth()
  const [status, setStatus] = useState<EZLynxStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  // Convertir Relay ID a numeric si es necesario
  const numericId = (() => {
    try {
      // Si es un número directo, úsalo
      if (/^\d+$/.test(leadId)) return leadId
      // Si es Relay ID (base64), decodifícalo
      return String(parseId(leadId))
    } catch {
      return leadId
    }
  })()

  const fetchStatus = useCallback(async () => {
    const token = getToken()
    if (!token || !numericId) return

    setLoading(true)
    try {
      const res = await axios.get(`${URL}/api/ezlynx/leads/${numericId}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setStatus(res.data)
    } catch {
      // Silencioso — la integración puede no estar habilitada
    } finally {
      setLoading(false)
    }
  }, [numericId])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleSync = async () => {
    const token = getToken()
    if (!token) return

    setSyncing(true)
    try {
      const res = await axios.post(
        `${URL}/api/ezlynx/leads/${numericId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (res.data.success) {
        // Mensaje del backend: se muestra literal, no se traduce en el front.
        toast.success(res.data.message)
        await fetchStatus()  // Refrescar badge
      } else {
        toast.error(res.data.error || intl.formatMessage(ezM.errorSyncFailed))
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        intl.formatMessage(ezM.errorSyncing)
      toast.error(msg)
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <span className="d-inline-flex align-items-center gap-1 text-muted fs-8">
        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
        <small>{ezlynx}</small>
      </span>
    )
  }

  if (!status) return null  // No está habilitado o hubo error silencioso

  return (
    <div className="d-inline-flex align-items-center gap-2">
      {status.synced ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <div className="text-start">
                <strong>{intl.formatMessage(ezM.synced)}</strong>
                <br />
                {intl.formatMessage(ezM.applicantId)} <code>{status.external_id}</code>
                {status.synced_at && (
                  <>
                    <br />
                    <small>{new Date(status.synced_at).toLocaleDateString()}</small>
                  </>
                )}
              </div>
            </Tooltip>
          }
        >
          <Badge
            bg="success"
            className="d-inline-flex align-items-center gap-1 cursor-pointer"
            style={{ fontSize: '0.7rem', padding: '3px 8px' }}
          >
            <i className="bi bi-check-circle-fill" style={{ fontSize: '0.65rem' }} />
            {ezlynx}
          </Badge>
        </OverlayTrigger>
      ) : (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{intl.formatMessage(ezM.notSyncedTooltip)}</Tooltip>}
        >
          <Badge
            bg="secondary"
            className="d-inline-flex align-items-center gap-1"
            style={{ fontSize: '0.7rem', padding: '3px 8px' }}
          >
            <i className="bi bi-cloud-slash" style={{ fontSize: '0.65rem' }} />
            {ezlynx}
          </Badge>
        </OverlayTrigger>
      )}

      {showButton && !status.synced && (
        <Button
          variant="light-primary"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="py-1 px-3"
          style={{ fontSize: '0.75rem' }}
        >
          {syncing ? (
            <><Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />{intl.formatMessage(ezM.syncing)}</>
          ) : (
            <><i className="bi bi-arrow-up-circle me-1" />{intl.formatMessage(ezM.syncAction)}</>
          )}
        </Button>
      )}

      {showButton && status.synced && (
        <Button
          variant="light"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="py-1 px-3"
          style={{ fontSize: '0.75rem' }}
          title={intl.formatMessage(ezM.resyncTooltip)}
        >
          {syncing ? (
            <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
          ) : (
            <i className="bi bi-arrow-repeat" />
          )}
        </Button>
      )}
    </div>
  )
}
